import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import * as Y from "yjs";
import type { AwarenessState, StoredBlock } from "@worktools/shared";
import {
  asksMap,
  blockProps,
  blocksMap,
  grantsForUser,
  grantsMap,
  openAskFor,
  pendingReviewsFor,
  reviewsMap,
  type Ask,
  type Grant,
  type Review,
} from "@worktools/shared";
import { useBoard } from "./BoardContext";

/** Frame data only — props changes must not re-render the whole board. */
export type BlockFrame = Pick<
  StoredBlock,
  "id" | "type" | "x" | "y" | "w" | "h" | "z" | "createdBy" | "accent" | "locked"
>;

function readFrames(doc: Y.Doc): BlockFrame[] {
  const frames: BlockFrame[] = [];
  for (const [id, map] of blocksMap(doc).entries()) {
    frames.push({
      id,
      type: map.get("type"),
      x: map.get("x"),
      y: map.get("y"),
      w: map.get("w"),
      h: map.get("h"),
      z: map.get("z") ?? 0,
      createdBy: map.get("createdBy"),
      accent: map.get("accent"),
      locked: map.get("locked"),
    });
  }
  return frames.sort((a, b) => a.z - b.z);
}

export function useBlockFrames(): BlockFrame[] {
  const { doc } = useBoard();
  const cache = useRef<{ key: string; value: BlockFrame[] }>({ key: "", value: [] });

  const subscribe = useCallback(
    (onChange: () => void) => {
      const map = blocksMap(doc);
      map.observeDeep(onChange);
      return () => map.unobserveDeep(onChange);
    },
    [doc],
  );

  const snapshot = useCallback(() => {
    const frames = readFrames(doc);
    const key = JSON.stringify(frames);
    if (key !== cache.current.key) cache.current = { key, value: frames };
    return cache.current.value;
  }, [doc]);

  return useSyncExternalStore(subscribe, snapshot);
}

export function useBlockProps<T extends Record<string, any>>(blockId: string): T {
  const { doc } = useBoard();
  const cache = useRef<{ key: string; value: T }>({ key: "", value: {} as T });

  const subscribe = useCallback(
    (onChange: () => void) => {
      const props = blockProps(doc, blockId);
      if (!props) return () => {};
      props.observeDeep(onChange);
      return () => props.unobserveDeep(onChange);
    },
    [doc, blockId],
  );

  const snapshot = useCallback(() => {
    const props = blockProps(doc, blockId);
    const value = (props ? props.toJSON() : {}) as T;
    const key = JSON.stringify(value);
    if (key !== cache.current.key) cache.current = { key, value };
    return cache.current.value;
  }, [doc, blockId]);

  return useSyncExternalStore(subscribe, snapshot);
}

/** Live handle to a shared prop (Y.Text / Y.Array / Y.Map) for mutation. */
export function useSharedProp<T>(blockId: string, key: string): T | undefined {
  const { doc } = useBoard();
  return useMemo(() => blockProps(doc, blockId)?.get(key) as T | undefined, [doc, blockId, key]);
}

export function useYText(blockId: string, key: string): { text: Y.Text | undefined; value: string } {
  const { doc } = useBoard();
  const text = useMemo(() => blockProps(doc, blockId)?.get(key) as Y.Text | undefined, [doc, blockId, key]);
  const [value, setValue] = useState(() => text?.toString() ?? "");

  useEffect(() => {
    if (!text) return;
    const update = () => setValue(text.toString());
    update();
    text.observe(update);
    return () => text.unobserve(update);
  }, [text]);

  return { text, value };
}

export type RemoteState = AwarenessState & { clientId: number };

export function useAwarenessStates(): RemoteState[] {
  const { awareness } = useBoard();
  const [states, setStates] = useState<RemoteState[]>([]);

  useEffect(() => {
    const update = () => {
      const next: RemoteState[] = [];
      for (const [clientId, state] of awareness.getStates()) {
        if (clientId === awareness.clientID) continue;
        const typed = state as AwarenessState;
        if (typed?.user) next.push({ ...typed, clientId });
      }
      setStates(next);
    };
    update();
    awareness.on("change", update);
    return () => awareness.off("change", update);
  }, [awareness]);

  return states;
}

export function useLocalAwareness(): (patch: Partial<AwarenessState>) => void {
  const { awareness } = useBoard();
  return useCallback(
    (patch: Partial<AwarenessState>) => {
      const current = (awareness.getLocalState() ?? {}) as AwarenessState;
      awareness.setLocalState({ ...current, ...patch });
    },
    [awareness],
  );
}

/** Only the shares of the signed in person — assistants never share a scope. */
export function useMyGrants(): Record<string, Grant> {
  const { doc, me } = useBoard();
  const cache = useRef<{ key: string; value: Record<string, Grant> }>({ key: "", value: {} });

  const subscribe = useCallback(
    (onChange: () => void) => {
      const map = grantsMap(doc);
      map.observe(onChange);
      return () => map.unobserve(onChange);
    },
    [doc],
  );

  const snapshot = useCallback(() => {
    const value = grantsForUser(doc, me.id);
    const key = JSON.stringify(value);
    if (key !== cache.current.key) cache.current = { key, value };
    return cache.current.value;
  }, [doc, me.id]);

  return useSyncExternalStore(subscribe, snapshot);
}

/** Review requests waiting for me — an assistant only ever asks its own owner. */
export function useMyPendingReviews(): Review[] {
  const { doc, me } = useBoard();
  const cache = useRef<{ key: string; value: Review[] }>({ key: "", value: [] });

  const subscribe = useCallback(
    (onChange: () => void) => {
      const map = reviewsMap(doc);
      map.observe(onChange);
      return () => map.unobserve(onChange);
    },
    [doc],
  );

  const snapshot = useCallback(() => {
    const value = pendingReviewsFor(doc, me.id);
    const key = JSON.stringify(value);
    if (key !== cache.current.key) cache.current = { key, value };
    return cache.current.value;
  }, [doc, me.id]);

  return useSyncExternalStore(subscribe, snapshot);
}

/** The question waiting for me, if Claude asked one. */
export function useMyOpenAsk(): Ask | null {
  const { doc, me } = useBoard();
  const cache = useRef<{ key: string; value: Ask | null }>({ key: "", value: null });

  const subscribe = useCallback(
    (onChange: () => void) => {
      const map = asksMap(doc);
      map.observe(onChange);
      return () => map.unobserve(onChange);
    },
    [doc],
  );

  const snapshot = useCallback(() => {
    const value = openAskFor(doc, me.id);
    const key = JSON.stringify(value);
    if (key !== cache.current.key) cache.current = { key, value };
    return cache.current.value;
  }, [doc, me.id]);

  return useSyncExternalStore(subscribe, snapshot);
}
